import { useNavigate } from "react-router-dom";
import { ProductGlyph } from "../components/ProductGlyph";
import { SectionHeading } from "../components/SectionHeading";
import { SiteFooter } from "../components/SiteFooter";
import { pageMeta } from "../config/site";
import { useDocumentTitle } from "../hooks/useDocumentTitle";

export function SecurityPage() {
  const navigate = useNavigate();
  useDocumentTitle(pageMeta.security.title, pageMeta.security.description);

  return (
    <main data-screen-label="安全与合规">
      <section className="inner-hero security-hero">
        <div className="inner-hero__meta">
          <span>SECURITY & PROFESSIONAL CONTROL</span>
        </div>
        <div className="security-hero__grid">
          <div className="security-hero__copy">
            <span className="page-kicker">独立部署与专业责任治理</span>
            <h1>
              <span>独立部署，边界清晰</span>
              <em>AI 提效，判断由人负责</em>
            </h1>
            <p>
              多项产品可独立部署，数据范围与访问边界随产品清晰说明；AI
              辅助检索、整理、分析和文档工作，关键结论始终由专业人员判断与复核。
            </p>
            <div className="security-hero__principles">
              <span>产品独立部署</span>
              <span>数据范围可说明</span>
              <span>按角色授权</span>
              <span>专业人员复核</span>
            </div>
          </div>
          <div className="security-hero__map" aria-label="安全与专业责任关系图">
            <div className="security-hero__map-art">
              <img
                src="/assets/security-governance-hero-imagegen-v2.png"
                alt="小加 AI 服务平台连接独立部署、数据边界、角色授权与人工复核"
                width={1200}
                height={900}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="security-responsibility page-section">
        <SectionHeading
          index="01"
          eyebrow="责任流程"
          title="AI 辅助到哪里，专业人员在何处接手"
          description="围绕一次具体任务，把资料进入、AI 辅助、人员复核和成果使用分开说明。"
        />
        <div className="security-responsibility__panel">
          <div className="security-responsibility__bar">
            <span>HUMAN IN CONTROL</span>
            <strong>关键结论由具备相应职责的专业人员确认</strong>
          </div>
          <div className="security-responsibility__flow">
            <article>
              <span className="security-flow-icon">
                <ProductGlyph type="boundary" />
              </span>
              <small>任务输入</small>
              <h3>业务资料进入当前任务</h3>
              <p>围绕产品正在处理的业务任务，明确需要使用的资料。</p>
            </article>
            <i aria-hidden="true">→</i>
            <article className="security-responsibility__ai">
              <span className="security-flow-icon">
                <ProductGlyph type="jia" />
              </span>
              <small>AI 辅助</small>
              <h3>检索、整理、分析与起草</h3>
              <p>形成线索、结构和工作草稿，缩短资料准备与初稿整理时间。</p>
            </article>
            <i aria-hidden="true">→</i>
            <article>
              <span className="security-flow-icon">
                <ProductGlyph type="human" />
              </span>
              <small>专业复核</small>
              <h3>核验依据并作出判断</h3>
              <p>专业人员检查来源、完善内容，并确认关键结论。</p>
            </article>
            <i aria-hidden="true">→</i>
            <article>
              <span className="security-flow-icon">
                <ProductGlyph type="review" />
              </span>
              <small>成果使用</small>
              <h3>确认后进入后续工作</h3>
              <p>辅助成果经复核确认，再进入项目交付与内部协作。</p>
            </article>
          </div>
        </div>
      </section>

      <section className="security-boundaries">
        <div className="page-section">
          <SectionHeading
            index="02"
            eyebrow="产品边界"
            title="独立启用一项产品，也能看清一套边界"
            description="智能标讯、标书智作等产品处理的任务与资料不同，因此按产品分别说明部署方式、数据范围和访问规则。"
          />
          <div className="security-boundaries__layout">
            <article className="security-boundaries__anchor">
              <span className="security-boundaries__anchor-icon">
                <ProductGlyph type="boundary" />
              </span>
              <small>INDEPENDENT PRODUCT</small>
              <h3>按产品独立启用</h3>
              <p>从当前需要的业务环节开始，不要求一次启用全部产品。</p>
              <div>
                <span>明确任务</span>
                <span>明确资料</span>
                <span>明确角色</span>
              </div>
            </article>
            <div className="security-boundaries__list">
              <article>
                <span>
                  <ProductGlyph type="retention" />
                </span>
                <div>
                  <small>数据边界</small>
                  <h3>会处理哪些资料，用于什么任务</h3>
                  <p>说明资料类型、处理目的，以及保存与删除等使用规则。</p>
                </div>
                <strong>资料 · 用途 · 保留</strong>
              </article>
              <article>
                <span>
                  <ProductGlyph type="access" />
                </span>
                <div>
                  <small>组织边界</small>
                  <h3>谁可以查看和使用</h3>
                  <p>围绕机构、成员和项目角色组织访问范围与协作空间。</p>
                </div>
                <strong>机构 · 成员 · 项目</strong>
              </article>
              <article>
                <span>
                  <ProductGlyph type="review" />
                </span>
                <div>
                  <small>技术与责任边界</small>
                  <h3>模型服务如何参与，结果由谁确认</h3>
                  <p>
                    说明涉及的模型或第三方技术服务，并明确专业人员的复核责任。
                  </p>
                </div>
                <strong>服务 · 范围 · 复核</strong>
              </article>
            </div>
          </div>
        </div>
      </section>

      <section className="security-checks">
        <div className="page-section">
          <SectionHeading
            index="03"
            eyebrow="启用前确认"
            title="把关键问题说清楚，再决定怎样使用"
            description="不依靠笼统承诺，用一组客户真正关心的问题逐项确认产品边界。"
            inverse
          />
          <div className="security-checks__grid">
            <article>
              <span>
                <ProductGlyph type="boundary" />
              </span>
              <small>资料范围</small>
              <h3>哪些业务资料会进入产品？</h3>
              <p>确认资料类型、使用目的和处理范围。</p>
            </article>
            <article>
              <span>
                <ProductGlyph type="retention" />
              </span>
              <small>保存与删除</small>
              <h3>文件是否保留，如何管理与删除？</h3>
              <p>确认保存方式、清理规则和可使用的删除路径。</p>
            </article>
            <article>
              <span>
                <ProductGlyph type="access" />
              </span>
              <small>账户权限</small>
              <h3>谁可以访问机构与项目资料？</h3>
              <p>确认机构、成员、角色和项目空间的访问范围。</p>
            </article>
            <article>
              <span>
                <ProductGlyph type="jia" />
              </span>
              <small>模型服务</small>
              <h3>是否使用模型或第三方技术服务？</h3>
              <p>确认服务类型、涉及的数据范围和对应说明。</p>
            </article>
            <article>
              <span>
                <ProductGlyph type="human" />
              </span>
              <small>专业责任</small>
              <h3>谁对关键结论作出判断？</h3>
              <p>确认 AI 辅助成果如何交给专业人员核验。</p>
            </article>
            <article>
              <span>
                <ProductGlyph type="review" />
              </span>
              <small>成果确认</small>
              <h3>辅助成果怎样进入业务流程？</h3>
              <p>确认核验、修改和确认完成后的使用方式。</p>
            </article>
          </div>
          <p className="security-checks__note">
            不同产品处理的任务和资料不同；数据保存、模型服务与第三方使用情况，以对应产品的正式说明为准。
          </p>
        </div>
      </section>

      <section className="security-next page-section">
        <div>
          <span>从一个产品开始</span>
          <h2>先把边界说明白，再选择适合团队的使用方式</h2>
          <p>
            回到首页查看各项产品解决的问题；正式启用前，再结合机构制度确认数据、权限和复核安排。
          </p>
        </div>
        <button
          className="button button--energy"
          type="button"
          onClick={() => navigate("/")}
        >
          返回首页查看产品 →
        </button>
      </section>
      <SiteFooter />
    </main>
  );
}
